import { useState, useEffect, useCallback, useRef } from 'react';
import localApiAdapter from '../../core/services/localApiAdapter';
import { useArticlesConfig } from './useArticlesConfig';

/** Fired when facturation catalog products change (create/update/delete). */
export const CATALOG_PRODUCTS_REFRESH_EVENT = 'catalog-products-changed';

export function dispatchCatalogProductsRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CATALOG_PRODUCTS_REFRESH_EVENT));
  }
}

/** Unified fetch for facturation catalog products shared across modules. */
export async function fetchCatalogProducts({ status, type } = {}) {
  const params = {};
  if (status) params.status = status;
  if (type) params.type = type;
  const response = await localApiAdapter.get('/facturation/products', Object.keys(params).length ? { params } : undefined);
  const data = response?.data ?? response;
  return Array.isArray(data) ? data : [];
}

/** Keep articles config hydrated while loading catalog products for cross-module forms. */
export function useCatalogProducts(options = {}) {
  const articlesConfig = useArticlesConfig();
  const { status, type, autoLoad = true } = options;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchCatalogProducts({ status, type });
      setProducts(rows);
      return rows;
    } catch (error) {
      console.error('Error loading catalog products:', error);
      setProducts([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [status, type]);

  useEffect(() => {
    if (!autoLoad) return undefined;
    void reload();
  }, [autoLoad, reload]);

  useEffect(() => {
    if (!autoLoad) return undefined;
    const onRefresh = () => {
      void reload();
    };
    window.addEventListener(CATALOG_PRODUCTS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CATALOG_PRODUCTS_REFRESH_EVENT, onRefresh);
  }, [autoLoad, reload]);

  return { products, loading, reload, articlesConfig };
}

/** Reload catalog products when Products/Services CUD fires refresh event. */
export function useCatalogProductsRefreshListener(reload, enabled = true) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    if (!enabled) return undefined;
    const onRefresh = () => {
      void reloadRef.current?.();
    };
    window.addEventListener(CATALOG_PRODUCTS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CATALOG_PRODUCTS_REFRESH_EVENT, onRefresh);
  }, [enabled]);
}
